import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core"

export type ReaderPinchDirection = "zoomIn" | "zoomOut"

export type ReaderPinchEvent = {
    direction: ReaderPinchDirection
    centerX: number
    centerY: number
}

interface ReaderGesturePlugin {
    setEnabled(options: { enabled: boolean }): Promise<void>
    addListener(
        eventName: "pinch",
        listener: (event: ReaderPinchEvent) => void,
    ): Promise<PluginListenerHandle>
}

const ReaderGesture = registerPlugin<ReaderGesturePlugin>("ReaderGesture")
let activeGestureListeners = 0

export function readerGestureIsAvailable() {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("ReaderGesture")
}

export async function enableReaderGestures(
    listener: (event: ReaderPinchEvent) => void,
): Promise<() => Promise<void>> {
    if (!readerGestureIsAvailable()) return async () => {}

    const handle = await ReaderGesture.addListener("pinch", listener)
    activeGestureListeners += 1
    try {
        await ReaderGesture.setEnabled({ enabled: true })
    } catch (error) {
        activeGestureListeners -= 1
        await handle.remove()
        throw error
    }

    let removed = false
    return async () => {
        if (removed) return
        removed = true
        activeGestureListeners -= 1
        try {
            await handle.remove()
        } finally {
            // Keyed reader pages overlap while their fade transitions run. Recheck after
            // removing this listener so a newly mounted page retains the global recognizer.
            if (activeGestureListeners === 0) {
                await ReaderGesture.setEnabled({ enabled: false })
            }
        }
    }
}
